import React from 'react';
import styled from 'styled-components';
import { Body7, Container, TextField } from '@ui';
import { TransparentFlatButton } from '@ui/buttons';
import { formatCount } from './format';

// The field's own surface, which @ui's TextField leaves transparent. Kept because this control sits
// on the query form's panel rather than on the page background, and a transparent field there reads
// as a gap rather than as somewhere to type.
const Field = styled.div`
  input {
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  }

  input::placeholder {
    color: ${({ theme }) => theme.themeColors.secondaryText};
  }
`;

// The note and its retry sit on one line so the retry reads as an answer to the note rather than a
// second, unrelated control under the field.
//
// A LIVE REGION, because the note changes under the analyst without them touching the field: the
// list finishes loading, it fails, or the kind filter above narrows it to nothing. A sighted user
// sees the sentence swap; without this nobody else learns anything happened — pressing Multi-spec
// announced "Multi-spec, pressed" and then silence, while the suggestion list emptied and the input
// quietly stopped being a combobox.
//
// `aria-live` rather than `role="status"`, which would be the more obvious spelling of the same
// thing. This note shares a form with CompanyFilter's resolve summary, which IS a status, and a
// second unnamed one beside it makes "the status region" ambiguous for anything that goes looking —
// which is exactly what it did to CompanyFilter's own test. The announcement behaviour is identical.
// It is NAMED for the same reason: an announcement that arrives with no idea what it is about is
// one more thing to work out.
const Note = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

// The note's TEXT, separately, so the field can point at the sentence without also swallowing the
// label of whatever button sits beside it — "Retry the feed list" is an action, not a description.
const NoteText = styled.span``;

// Suggestions, not a gate. A native datalist keeps the field a text input — feed ids are long
// enough to want pasting, and one that isn't listed yet (a brand-new feed, a typo worth seeing
// rejected) must still be submittable. A failed list is reported as a quiet note, since the field
// is fully usable without it.
//
// The copy stays short of calling the list a roster, because `GET /feeds` isn't one: it omits
// derived source-table feeds by design, and a bad multi-input registry fails open — the score feeds
// still come back, the multi-input ones silently don't. So an id that isn't in the list is a
// perfectly ordinary thing to submit, and the server is the only authority on whether it exists.
//
// That fail-open path also lands on an EMPTY list, indistinguishable from a genuinely empty one,
// which is why the empty state no longer blames the account and offers the same retry the error
// state does.
//
// `hiddenCount` and `onShowAll` are how the kind filter reaches this component: it filters the list
// UPSTREAM and says how many it took away, rather than passing the filter down and having the picker
// apply it. The picker's job is to describe the list it was given, and a count it cannot see is a
// count it cannot explain — "no suggestions" and "no suggestions OF THIS KIND" are different
// sentences, and only the second one has a way out.
export const FeedPicker = ({
  id,
  value,
  onChange,
  onKeyDown,
  feeds,
  isLoading,
  error,
  onRetry,
  hiddenCount = 0,
  onShowAll,
}) => {
  const options = feeds || [];
  const canRetry = Boolean(onRetry) && !isLoading;
  // Per-instance, where it was a module constant: two pickers on one page would otherwise have
  // declared one datalist id between them, and `list` would resolve to whichever rendered first.
  const datalistId = React.useId();
  // Named so the field can carry it as its own description. Without this the note is floating text
  // near the input: a screen reader tabbing onto the field announced "Feed, edit text" and nothing
  // about why there were no suggestions in it.
  const noteId = React.useId();

  // The note, as a SENTENCE and an ACTION, decided here rather than in the tree. Two reasons, and
  // both are about what a screen reader is handed: the sentence is what the field points at, so a
  // button's label must not be inside it, and the branches have to be exclusive and exhaustive in
  // one readable place — "the list came back empty" and "the filter emptied it" are different facts
  // with different repairs, and the wrong one is a plain untruth.
  const note = () => {
    if (isLoading) return { text: 'Loading the feed list…' };
    if (error) {
      return {
        text: 'Couldn’t load the feed list — type or paste a feed id instead.',
        action: canRetry && <TransparentFlatButton onClick={onRetry}>Retry the feed list</TransparentFlatButton>,
      };
    }
    if (options.length > 0) {
      const shown = formatCount(options.length);
      const tail = 'start typing to see them. A feed missing from the list is still worth submitting.';
      return {
        text:
          hiddenCount > 0
            ? `${shown} of ${formatCount(options.length + hiddenCount)} suggestions — ${tail}`
            : `${shown} suggestions — ${tail}`,
      };
    }
    // Empty because of the FILTER, which is a different fact from an empty list and must not borrow
    // its sentence: the list arrived perfectly well, and the retry under it would be offering to
    // re-fetch something that never failed. The way out is the filter, so that is what this offers.
    if (hiddenCount > 0) {
      return {
        text: `No feeds of that kind in the list — ${formatCount(
          hiddenCount
        )} hidden by the filter. Switch to All, or type a feed id.`,
        action: onShowAll && <TransparentFlatButton onClick={onShowAll}>Show all feeds</TransparentFlatButton>,
      };
    }
    return {
      text: 'The feed list came back empty — type or paste a feed id.',
      action: canRetry && <TransparentFlatButton onClick={onRetry}>Retry the feed list</TransparentFlatButton>,
    };
  };

  const { text, action } = note();

  return (
    <div>
      <Field>
        <TextField
          id={id}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder="fundamental_earnings_screen_v1"
          spellCheck={false}
          autoComplete="off"
          list={options.length ? datalistId : undefined}
          aria-describedby={noteId}
        />
      </Field>
      {options.length > 0 && (
        <datalist id={datalistId}>
          {options.map((feedId) => (
            <option key={feedId} value={feedId} />
          ))}
        </datalist>
      )}
      <Container top={4}>
        <Note aria-live="polite" aria-label="Feed list">
          <NoteText id={noteId}>
            <Body7 isLowContrast>{text}</Body7>
          </NoteText>
          {action || null}
        </Note>
      </Container>
    </div>
  );
};
